// frontend/src/components/LOS/Niyogin/NiyoginCreateCustomer.tsx
// Manual-trigger page: shows a button. User clicks → API fires → onDone() advances journey
// only if Niyogin returns HTTP 200. On failure the error is shown and the user stays here.

import React, { useState } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import JourneyHeader from "@/components/JourneyHeader";

interface Props {
  value?: any;
  onChange?: (val: any) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  onDone?: () => void;
}

const NiyoginCreateCustomer: React.FC<Props> = ({ onDone }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/trigger_create_niyogin_customer/`, {});
      // Backend only returns success=true when Niyogin returned HTTP 200
      if (res.data?.success === true) {
        setDone(true);
        onDone?.();
      } else {
        // Unexpected: backend returned 200 but success=false
        setError(res.data?.error || "Customer creation failed. Please try again.");
      }
    } catch (err: any) {
      // axios throws on 4xx/5xx — extract the Niyogin error message from response body
      const niyoginMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : String(err));
      console.error("[NiyoginCreateCustomer] Error:", niyoginMsg, err?.response?.data);
      setError(niyoginMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-intellend-white">
      <JourneyHeader />
      <main className="container mx-auto px-4 py-8 max-w-md">
        <div className="bg-white shadow-md rounded-lg p-8 space-y-6">
          {/* Icon + Title */}
          <div className="text-center space-y-2">
            <div className="text-5xl">👤</div>
            <h2 className="text-xl font-bold text-gray-800">Create Customer Profile</h2>
            <p className="text-sm text-gray-500">
              We will register your profile with our lending partner Niyogin to begin your loan application.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
              <p className="text-sm font-semibold text-red-700">❌ Registration Failed</p>
              <p className="text-sm text-red-600">{error}</p>
              <p className="text-xs text-red-500">Please correct the issue and try again.</p>
            </div>
          )}

          {/* Success */}
          {done && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700 text-center">
              ✅ Customer profile created successfully!
            </div>
          )}

          {/* Action Button */}
          {!done && (
            <button
              onClick={handleClick}
              disabled={loading}
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Creating Profile…
                </span>
              ) : (
                error ? "Retry" : "Create Customer Profile"
              )}
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default NiyoginCreateCustomer;
